/**
 * The message area at the foot of the sidebar.
 *
 * What this replaced was a modal dialog, and the reasons for the change are worth keeping.
 * A dialog takes the whole page to say that one thing went wrong, has to be dismissed
 * before anything else can be done, and — being modal — hides the very panel the message
 * is about. For a report of something that has already happened, all three are costs with
 * nothing bought. Something that needs an *answer* still gets a dialog: a question cannot
 * be asked by a note that can be ignored.
 *
 * Directly above the storage figure and the share button, so that the share button's
 * failures appear beside the button that produced them, and so that the stack grows upwards
 * into the sidebar's own spare room rather than pushing the controls about beneath it. The
 * newest is at the bottom, nearest what caused it; older ones ride up. Once the stack
 * reaches the model list both it and the list scroll, which is the stylesheet's half of the
 * arrangement.
 *
 * Rendering is incremental rather than wholesale. Replacing the contents on every change
 * would have a screen reader announce every message still showing each time a new one
 * arrives, so entries are added and removed one at a time and the ones already there are
 * left alone.
 *
 * ## Messages take themselves away
 *
 * Each has a countdown, of a length `dwellFor` works out from how much there is to read.
 * The one thing that must not happen is a message disappearing out from under somebody
 * reading it, and this is where that is prevented: **the clock stops while the panel is
 * under the pointer or holds the focus.** Reading it is therefore not a race, and reaching
 * for a dismiss button cannot be beaten to it by the message going of its own accord.
 */

import { element } from './dom.js';
import {
  dwellFor,
  NoticeList,
  type Notice,
  type NoticeInput,
  type NoticeLevel,
} from './noticeList.js';

/** How much of a message names the button that dismisses it. */
const SUMMARY_LENGTH = 48;

/**
 * What a message shows in place of itself once the sidebar is folded away.
 *
 * Three characters everybody has, in a coloured disc drawn by the stylesheet, rather than
 * three glyphs from the parts of Unicode a font may or may not carry — a message that
 * reports a problem is no place for a missing-glyph box. Which of the three it is carries
 * the same meaning the colour does, so neither is the only way to tell them apart.
 */
const SYMBOLS: Record<NoticeLevel, string> = {
  error: '×',
  warning: '!',
  info: 'i',
};

/**
 * The least a message gets once attention leaves it, in milliseconds.
 *
 * Something whose time ran out while it was being read must not vanish in the same instant
 * the pointer moves off it: that reads as having broken something rather than as a message
 * ending. A moment's grace is enough to look like a decision.
 */
const GRACE_MS = 1500;

/** A message's remaining time, and the timer running it down if one is. */
interface Countdown {
  /** Milliseconds still to run when it was last started. */
  remaining: number;
  /** When it was last started, so that stopping can work out what is left. */
  startedAt: number;
  /** Absent while the clock is stopped. */
  timer: ReturnType<typeof setTimeout> | undefined;
}

export class NoticeBoard {
  private readonly root = element('notices');
  private readonly list = new NoticeList();
  private readonly rendered = new Map<string, HTMLElement>();
  private readonly countdowns = new Map<string, Countdown>();

  /** Whether the panel is under the pointer, holds the focus, or neither. */
  private hovered = false;
  private focused = false;
  private reading = false;

  constructor() {
    // Both, not either: a pointer resting on the panel and a keyboard moving through it are
    // the same statement, and they can overlap, so each is tracked separately and the
    // clocks follow whether *anything* is attending to it.
    this.root.addEventListener('pointerenter', () => {
      this.hovered = true;
      this.attend();
    });
    this.root.addEventListener('pointerleave', () => {
      this.hovered = false;
      this.attend();
    });
    this.root.addEventListener('focusin', () => {
      this.focused = true;
      this.attend();
    });
    this.root.addEventListener('focusout', () => {
      this.focused = false;
      this.attend();
    });
  }

  /** Post a message, or recognise one already showing. */
  show(input: NoticeInput): void {
    const notice = this.list.add(input);

    // Asked before the page is brought into line, not after: afterwards everything is on
    // screen, new entries included, and the flash below would fire for every message
    // rather than only for the ones that were already there.
    const wasShowing = this.rendered.has(notice.id);

    this.sync();

    if (wasShowing) {
      // Existing entries are not redrawn, so a repeat of something already up would be
      // silent. Nothing needs to change on screen — the message is already there — but a
      // moment's emphasis says the button was heard.
      this.acknowledge(notice.id);

      // And it starts again. Reported a second time it is news a second time, and it may
      // have been a moment from going.
      this.arm(notice);
    }
  }

  /** Convenience for the common case, which is something having gone wrong. */
  error(message: string, detail?: string): void {
    this.show({ level: 'error', message, ...(detail === undefined ? {} : { detail }) });
  }

  clear(): void {
    this.list.clear();
    this.sync();
  }

  /** Bring the page into line with the list, touching only what actually changed. */
  private sync(): void {
    const current = new Set(this.list.notices.map((notice) => notice.id));

    for (const [id, node] of this.rendered) {
      if (current.has(id)) continue;
      node.remove();
      this.rendered.delete(id);
      this.disarm(id);
    }

    let added = false;
    for (const notice of this.list.notices) {
      if (this.rendered.has(notice.id)) continue;
      const node = this.draw(notice);
      this.rendered.set(notice.id, node);
      this.root.append(node);
      this.arm(notice);
      added = true;
    }

    this.root.hidden = this.rendered.size === 0;

    // The newest is at the bottom, which is off the end once the stack is tall enough to
    // scroll. `nearest` rather than the foot of the panel: a message too tall for the box
    // would otherwise be shown from its last line, with its heading and its dismiss button
    // above the top edge.
    //
    // Not while somebody is reading, though. Yanking the panel out from under them to show
    // something they have not asked about yet is exactly the interruption all of this is
    // arranged to avoid.
    if (!added || this.reading) return;

    const newest = this.root.lastElementChild;
    if (newest instanceof HTMLElement) {
      newest.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /**
   * Start a message's countdown, or start it again.
   *
   * Set but not run while the panel is being read. It picks up from the full dwell when
   * attention leaves, which is the right answer for a message that arrived while somebody
   * was looking at the one above it.
   */
  private arm(notice: Notice): void {
    this.disarm(notice.id);

    const countdown: Countdown = {
      remaining: dwellFor(notice),
      startedAt: Date.now(),
      timer: undefined,
    };
    this.countdowns.set(notice.id, countdown);

    if (!this.reading) this.run(notice.id, countdown);
  }

  private run(id: string, countdown: Countdown): void {
    countdown.startedAt = Date.now();
    countdown.timer = setTimeout(() => {
      this.countdowns.delete(id);
      this.list.dismiss(id);
      this.sync();
    }, countdown.remaining);
  }

  private disarm(id: string): void {
    const countdown = this.countdowns.get(id);
    if (countdown?.timer !== undefined) clearTimeout(countdown.timer);
    this.countdowns.delete(id);
  }

  /** Stop or restart every clock, according to whether anything is attending to the panel. */
  private attend(): void {
    const reading = this.hovered || this.focused;
    if (reading === this.reading) return;
    this.reading = reading;

    if (reading) {
      const now = Date.now();
      for (const countdown of this.countdowns.values()) {
        if (countdown.timer === undefined) continue;
        clearTimeout(countdown.timer);
        countdown.timer = undefined;
        countdown.remaining -= now - countdown.startedAt;
      }
      return;
    }

    for (const [id, countdown] of this.countdowns) {
      countdown.remaining = Math.max(countdown.remaining, GRACE_MS);
      this.run(id, countdown);
    }
  }

  private draw(notice: Notice): HTMLElement {
    const item = document.createElement('div');
    item.className = `notice notice--${notice.level}`;

    // On the entry rather than on the container: an error is worth interrupting for, and
    // the rest are not. A live region on the container would announce every level the
    // same way, and would speak again whenever anything in it changed.
    if (notice.level === 'error') item.setAttribute('role', 'alert');

    // For the folded sidebar, where the symbol is all there is room for. Set on the whole
    // entry rather than on the symbol, so that it also answers a pointer resting anywhere
    // on the message — and it costs nothing while the words are showing.
    item.title = fullText(notice);

    // Shown only while folded, and marked decorative because it says exactly what the text
    // beside it says. Kept in the markup either way rather than added and removed with the
    // fold: the board draws entries once and does not redraw them, so an entry that
    // outlived a fold would be one with no symbol to show.
    const symbol = document.createElement('span');
    symbol.className = 'notice__symbol';
    symbol.textContent = SYMBOLS[notice.level];
    symbol.setAttribute('aria-hidden', 'true');
    item.append(symbol);

    const body = document.createElement('div');
    body.className = 'notice__body';

    if (notice.title !== undefined) {
      const title = document.createElement('strong');
      title.className = 'notice__title';
      title.textContent = notice.title;
      body.append(title);
    }

    const message = document.createElement('p');
    message.className = 'notice__message';
    message.textContent = notice.message;
    body.append(message);

    if (notice.detail !== undefined) {
      const detail = document.createElement('p');
      detail.className = 'notice__detail';
      detail.textContent = notice.detail;
      body.append(detail);
    }

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'notice__dismiss';
    dismiss.textContent = '×';
    // Named by what it dismisses. A panel of buttons all called "Dismiss" tells somebody
    // moving between them by keyboard or by voice nothing about which is which. Shortened,
    // because the message is read out beside the button already and hearing the whole of
    // it a second time to find out what the button does is worse than hearing enough.
    dismiss.setAttribute('aria-label', `Dismiss: ${summarise(notice)}`);
    dismiss.addEventListener('click', () => {
      this.list.dismiss(notice.id);
      this.sync();
    });

    item.append(body, dismiss);
    return item;
  }

  /**
   * Flash an entry that was reported again.
   *
   * Removing and re-adding the class is what restarts the animation; without the reflow
   * between them the browser coalesces the two and nothing happens.
   */
  private acknowledge(id: string): void {
    const node = this.rendered.get(id);
    if (!node) return;

    node.classList.remove('notice--repeated');
    void node.offsetWidth;
    node.classList.add('notice--repeated');
  }
}

/** Everything a message says, on one line, for a tooltip. */
function fullText(notice: Notice): string {
  return [notice.title, notice.message, notice.detail]
    .filter((part) => part !== undefined)
    .join(' — ');
}

/**
 * Enough of a message to tell one dismiss button from another.
 *
 * The title where there is one, since that is what it is for. Otherwise the opening of
 * the message, cut at a word so the result reads as a phrase rather than as a fragment.
 */
function summarise(notice: Notice): string {
  if (notice.title !== undefined) return notice.title;
  if (notice.message.length <= SUMMARY_LENGTH) return notice.message;

  const cut = notice.message.slice(0, SUMMARY_LENGTH);
  const lastSpace = cut.lastIndexOf(' ');
  return `${lastSpace > 0 ? cut.slice(0, lastSpace) : cut}…`;
}
